// SearchDialog.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, TextField, InputAdornment, IconButton, List, ListItemButton, ListItemText, ListItemIcon, Typography, Box, Backdrop, useTheme,} from "@mui/material";
import { Search as SearchIcon, History as HistoryIcon, Clear as ClearIcon,} from "@mui/icons-material";

/**
 * SearchDialog
 *
 * Props:
 * - open (bool)                : whether dialog is open
 * - onClose (fn)               : function to call when dialog should close
 * - onNavigate (fn)            : function(link) => navigate to link (caller provides navigate)
 * - initialQuery (string)      : optional initial search text
 * - menuItems (array)          : [{ label, link }, ...] the searchable list
 * - recentlyVisited (array)    : optional recent items (same shape as menuItems)
 * - isMobile (bool)            : whether to render fullScreen (forwarded from parent)
 *
 * Important notes:
 * - This component intentionally DOES NOT call stopPropagation() on keyboard events,
 *   so 'Escape' will bubble to the Dialog and close it normally.
 * - Dialog/backdrop zIndex is raised to sit above other custom overlays in the app,
 *   preventing other popovers from blocking backdrop clicks.
 * - When a search result is selected we:
 *     1) blur the active element to avoid focus-based re-opening races
 *     2) close the dialog immediately
 *     3) wait a short timeout to let the modal unmount and then call onNavigate(link)
 *     This prevents the sidebar input or other focus handlers from immediately re-opening the dialog.
 */
export default function SearchDialog({ open, onClose, onNavigate, initialQuery = "", menuItems = [], recentlyVisited = [], isMobile = false, }) {
    const theme = useTheme();
    const [query, setQuery] = useState(initialQuery || "");
    const inputRef = useRef(null);

    // Keep query in sync when open or when initialQuery changes
    useEffect(() => {
        setQuery(initialQuery || "");
    }, [initialQuery, open]);

    // compute filtered items
    const filteredItems = useMemo(() => {
        if (!query) return menuItems;
        const q = query.trim().toLowerCase();
        return menuItems.filter((it) => it.label.toLowerCase().includes(q));
    }, [menuItems, query]);

    // Centralized close handler that parent can reuse
    const handleClose = () => {
        // Clear query and notify parent
        setQuery("");
        if (onClose) onClose();
    };

    // Called when user selects an item
    // const handleSelect = (item) => {
    //     try {
    //         // 1) Blur active element to avoid focus-based re-open race (important)
    //         if (document.activeElement && typeof document.activeElement.blur === "function") {
    //             document.activeElement.blur();
    //         }

    //         // 2) Close dialog immediately (so backdrop/unmount occurs)
    //         setQuery("");
    //         if (onClose) onClose();

    //         // 3) Small delay before navigation ensures dialog unmount and focus changes have settled.
    //         //    Using ~80ms avoids rare race conditions where focus returns to a sidebar input which would
    //         //    re-open the dialog while route change is in progress.
    //         setTimeout(() => {
    //             try {
    //                 if (onNavigate && item && item.link) onNavigate(item.link);
    //             } catch (navErr) {
    //                 // still swallow to avoid breaking UI
    //                 // eslint-disable-next-line no-console
    //                 console.error("Navigation error in SearchDialog:", navErr);
    //             }
    //         }, 80);
    //     } catch (err) {
    //         // eslint-disable-next-line no-console
    //         console.error("SearchDialog.handleSelect error:", err);
    //     }
    // };

    const handleSelect = (item) => {
        // 1. Blur to prevent re-focus
        document.activeElement?.blur();

        // 2. Close dialog IMMEDIATELY
        setQuery("");
        onClose();  // This triggers setSearchOpen(false) in parent

        // 3. Navigate after a tiny delay — ensures dialog is gone
        setTimeout(() => {
            if (onNavigate && item?.link) {
                onNavigate(item.link);
            }
        }, 100);
    };

    // Keyboard handler on the TextField:
    // - DO NOT stop propagation. We only intercept Tab if you want Tab to close the dialog.
    // - Let Escape bubble so MUI can emit reason === 'escapeKeyDown' to onClose if necessary.
    const handleKeyDown = (e) => {
        if (e.key === "Tab") {
            // If your app expects Tab to close the dialog, handle it here.
            // We close and prevent default so focus doesn't jump unexpectedly.
            e.preventDefault();
            handleClose();
            return;
        }
        // do NOT call e.stopPropagation() here — it would block Escape from reaching Dialog.
    };

    // Because we raised the Dialog's z-index, ensure Backdrop also sits above other overlays.
    // We rely on onClose(reason) in parent — parent can ignore reason but this component uses onClose callback.
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile} closeAfterTransition PaperProps={{ sx: { borderRadius: 3, boxShadow: 3, maxHeight: "80vh" } }} >
            <DialogTitle sx={{ p: 2 }}>
                <TextField
                    // Use ref if parent needs access; also helpful for debugging
                    inputRef={inputRef} autoFocus fullWidth placeholder="Search menu..." value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={handleKeyDown}
                    InputProps={{
                        startAdornment: ( <InputAdornment position="start"> <SearchIcon /> </InputAdornment> ),
                        endAdornment: query ? (
                            <InputAdornment position="end">
                                <IconButton aria-label="Clear search" size="small"
                                    onClick={() => {
                                        setQuery("");
                                        // keep focus in the input so user can continue typing after clearing
                                        if (inputRef.current && typeof inputRef.current.focus === "function") {
                                            inputRef.current.focus();
                                        }
                                    }}
                                >
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                {/* Show recently visited only when the search box is empty */}
                {(!query || query.trim() === "") && recentlyVisited && recentlyVisited.length > 0 && (
                    <Box sx={{ p: 2, pb: 0 }}>
                        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: "text.secondary" }}>
                            Recently Visited
                        </Typography>
                        {recentlyVisited.map((item, i) => (
                            <ListItemButton key={`recent-${i}`} onClick={() => handleSelect(item)}>
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    <HistoryIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        ))}
                    </Box>
                )}

                <List>
                    {filteredItems.length === 0 ? (
                        <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary" }}> {query ? "No results found" : "Start typing to search"} </Typography>
                    ) : (
                        filteredItems.map((item, i) => (
                            <ListItemButton key={`result-${i}`} onClick={() => handleSelect(item)}> <ListItemText primary={item.label} secondary={item.link} /> </ListItemButton>
                        ))
                    )}
                </List>
            </DialogContent>
        </Dialog>
    );
}